<template>
  <div>
    <image-field
      :mealImageUrl="mealImageUrl"
      :mealOriginUrl="mealOriginUrl"
      accept="image/*"
      @upload="uploadImage"
      @change="changeImage"
      :editMode="isAdmin"
      :loading="uploadingImage"
      :errors="mealImageErrors"
      :imageTitle="mealName"
      :cropData="mealImageCrop"
    />
    <textarea-field
      :label="$t('Name *')"
      :placeholder="$t('Tasty name')"
      v-model="mealName"
      :errors="mealName_errorsIfTouched"
      :disabled="!isAdmin"
      :rows="numberOfLines(mealName)"
    />
    <input-field
      :label="$t('Description')"
      :placeholder="$t('Nice description')"
      v-model="mealDescription"
      :errors="mealDescription_errorsIfTouched"
      :disabled="!isAdmin"
      maxlength="65"
    />
    <number-field
      :label="$t('Price ({0}) *', currency)"
      :placeholder="$t('Enter price')"
      v-model="mealPrice"
      :errors="mealPrice_errorsIfTouched"
      :disabled="!isAdmin"
    />
    <tag-list
      :label="$t('Meal Categories')"
      :disabled="!isAdmin"
      :options="categories"
      v-model="mealCategories"
    />
    <icons-selector
      :label="$t('Meal Badges')"
      :disabled="!isAdmin"
      :options="badges"
      v-model="mealBadges"
      :errors="mealBadges_errorsIfTouched"
    />
    <icons-selector
      :label="$t('Meal Allergens')"
      :disabled="!isAdmin"
      :options="allergens"
      v-model="mealAllergens"
    />

    <div class="help is-danger column is-8" v-if="actionStatus == 'Timeout'">
      {{
        $t(
          "Action takes too long. It will complete automatically once connection is reestablished. Do not close the app."
        )
      }}
    </div>

    <div class="field is-grouped is-pulled-left">
      <div class="control" v-if="isAdmin">
        <button
          class="button is-primary"
          :class="{ 'is-loading': actionStatus == 'Save' }"
          :disabled="
            !group_valid || actionStatus || uploadingImage || !group_dirty
          "
          @click="submitForm()"
        >
          {{ $t("Save") }}
        </button>
      </div>
      <div class="control">
        <button class="button" @click="cancelForm">{{ $t("Cancel") }}</button>
      </div>
    </div>
    <div class="field is-grouped is-pulled-right" v-if="isAdmin">
      <div class="control" v-if="mealId">
        <button
          class="button is-danger"
          :class="{ 'is-loading': actionStatus == 'Remove' }"
          :disabled="actionStatus || uploadingImage"
          @click="openConfirmation"
        >
          {{ $t("Remove") }}
        </button>
      </div>
    </div>

    <ModalConfirmation
      :is-open.sync="deletingMeal"
      :title="$t('Are you sure you want to delete this meal?')"
      :confirmText="$t('Delete')"
      @confirm="removeMeal(mealId)"
    >
      {{
        $t(
          "Once meal {0} is deleted, you will not be able to recover it!",
          mealName
        )
      }}
    </ModalConfirmation>
  </div>
</template>

<script>
import { state } from "@/store"
import imageService from "@/service/image-service"
import {
  InputField,
  TextareaField,
  NumberField,
  ImageField,
  TagList,
  IconsSelector
} from "@/components/form"
import { ModalConfirmation } from "@/components/modal"
import {
  validator,
  required,
  size,
  maxSize,
  maxSelected,
  regexp,
  nonBlank
} from "@/validator"
import { cleanObject } from "@/utils"
import { ALLERGENS, BADGES } from "@/service/meal-constants.js"

export default {
  name: "meal-form",
  mixins: [validator],
  props: ["meal", "actionStatus"],
  components: {
    InputField,
    TextareaField,
    NumberField,
    ImageField,
    TagList,
    IconsSelector,
    ModalConfirmation
  },
  data() {
    return {
      deletingMeal: false,
      uploadingImage: false,
      mealImageErrors: [],
      mealId: this.meal.id,
      mealName: this.meal.name,
      mealDescription: this.meal.description,
      mealPrice: this.meal.price,
      mealImageUrl: this.meal.imageUrl,
      mealImageSdUrl: this.meal.imageSdUrl,
      mealOriginUrl: this.meal.originUrl,
      mealImageCrop: this.meal.imageCrop,
      mealAllergens: this.meal.allergens,
      mealBadges: this.meal.badges,
      mealCategories: this.meal.categories
    }
  },
  computed: {
    currency() {
      return state.project.data.currency
    },
    isAdmin() {
      return state.isAdmin
    },
    categories() {
      return state.project.data.availableCategories
    },
    allergens() {
      return ALLERGENS
    },
    badges() {
      return BADGES
    }
  },
  watch: {
    meal(meal) {
      this.uploadingImage = false
      this.mealImageErrors = []
      this.mealId = meal.id
      this.mealName = meal.name
      this.mealDescription = meal.description
      this.mealPrice = meal.price
      this.mealImageUrl = meal.imageUrl
      this.mealImageSdUrl = meal.imageSdUrl
      this.mealThumbUrl = meal.thumbUrl
      this.mealOriginUrl = meal.originUrl
      this.mealAllergens = meal.allergens
      this.mealBadges = meal.badges
      this.mealCategories = meal.categories
    }
  },
  validations: {
    mealName: [required, nonBlank, size(2, 160)],
    mealDescription: [maxSize(64)],
    mealPrice: [required, regexp(/^[0-9]*[.]?[0-9]?[0-9]?$/, "0.00")],
    mealBadges: [maxSelected(3)],
    mealAllergens: [],
    mealImageUrl: [],
    mealOriginUrl: [],
    mealThumbUrl: [],
    mealImageSdUrl: [],
    mealCategories: []
  },
  methods: {
    submitForm() {
      const meal = cleanObject({
        id: this.mealId,
        name: this.mealName,
        description: this.mealDescription,
        price: this.mealPrice,
        imageUrl: this.mealImageUrl,
        thumbUrl: this.mealThumbUrl,
        imageSdUrl: this.mealImageSdUrl,
        originUrl: this.mealOriginUrl,
        imageCrop: this.mealImageCrop,
        allergens: this.mealAllergens,
        badges: this.mealBadges,
        categories: this.mealCategories
      })
      if (this.mealId === undefined) {
        this.$emit("create", meal)
      } else {
        this.$emit("update", meal)
      }
    },
    cancelForm() {
      this.$emit("clearErrors")
      this.$emit("cancel")
    },
    removeMeal(mealId) {
      this.$emit("remove", mealId)
    },
    changeImage(cropData) {
      this.mealImageErrors = []
      this.uploadingImage = true
      const url = this.mealOriginUrl
      if (!url || !cropData) {
        this.$logger.error("no-image-crop")
        return
      }
      imageService
        .cropImage(url, cropData)
        .then(imageUrls => {
          const suffix = "&d=" + Date.now()
          this.mealImageUrl = imageUrls.imageUrl + suffix
          this.mealThumbUrl = imageUrls.thumbUrl + suffix
          this.mealOriginUrl = imageUrls.originUrl
          this.mealImageSdUrl = imageUrls.imageSdUrl
          this.mealImageCrop = cropData
          this.uploadingImage = false
        })
        .catch(e => {
          this.uploadingImage = false
          this.$logger.error("edit-image-error", e)
          switch (e && e.message) {
            case "offline":
              this.mealImageErrors = [
                "Image edit failed, missing network connection"
              ]
              break
            default:
              this.mealImageErrors = ["Image upload failed"]
              break
          }
        })
    },
    uploadImage(file) {
      this.mealImageErrors = []
      if (file === undefined) {
        this.$logger.info("no image given")
        return
      }
      this.uploadingImage = true
      imageService
        .uploadImage(file.imageRef, file)
        .then(imageUrls => {
          this.uploadingImage = false
          this.mealImageUrl = imageUrls.imageUrl
          this.mealThumbUrl = imageUrls.thumbUrl
          this.mealOriginUrl = imageUrls.originUrl
          this.mealImageSdUrl = imageUrls.imageSdUrl
          this.mealImageCrop = undefined
          // TODO: success info
        })
        .catch(e => {
          this.uploadingImage = false
          this.$logger.error("upload-image-error", e)
          switch (e && e.message) {
            case "offline":
              this.mealImageErrors = [
                "Image upload failed, missing network connection"
              ]
              break
            case "fileTooBig":
              this.mealImageErrors = [
                "Image upload failed, file is too large (max 3MB)"
              ]
              break
            default:
              this.mealImageErrors = ["Image upload failed"]
              break
          }
        })
    },
    openConfirmation() {
      this.$emit("clearErrors")
      this.deletingMeal = true
    },
    numberOfLines(str = "") {
      return str.split(/\r\n|\r|\n/).length
    }
  }
}
</script>
